<template>
  <div>
    <h1 class="m-3">
      {{ $t("global.exportInProgress") }}
    </h1>
    <div v-for="sheetId of sheetIds" :id="sheetId" :key="sheetId" style="display: none">
      <table>
        <thead>
          <tr>
            <th :colspan="titleSubtitleColspan" data-a-wrap="true" data-f-bold="true" data-f-sz="24">
              {{ title }}
            </th>
          </tr>
          <tr v-for="subtitle of subtitles" :key="subtitle">
            <th :colspan="titleSubtitleColspan" data-a-wrap="true" data-f-sz="12">
              {{ subtitle }}
            </th>
          </tr>
        </thead>
        <!-- Ligne vide pour faire une ligne d'espace -->
        <tbody>
          <tr>
            <td />
          </tr>
        </tbody>
      </table>
      <div v-for="data of datasGroupedBySheetId[sheetId]" :key="data.tableNumber">
        <table-renderer class="export" :table-number="data.tableNumber" :data-table="data.dataTable"
          :dimension-fields="data.dimensionFields" :measure-fields="data.measureFields"
          :group-headers="data.groupHeaders" :table-header="data.tableHeader"
          :default-sorted-column="data.defaultSortedColumn" :top-group-headers="data.topGroupHeaders"
          :averages-data="data.averagesData" :totals-data="data.totalsData" :data-ready="dataReady"
          :hide-groups="hideGroupsByTableNumber[data.tableNumber] === true" :custom-table-class="data.customTableClass"
          :excel-attr-th="excelAttrTh" :export-mode="true" />
        <!-- Table vide pour faire une ligne d'espace -->
        <table>
          <tbody>
            <tr>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import ExcelJS from "exceljs";
import TableToExcel from "@linways/table-to-excel";
import TableRenderer from "@/components/TableRenderer.vue";
import TableTools from "@/utils/tableTools";
import { groupBy } from "lodash";
import { excelStyles } from "@/scss/excelStyles";
import { ExportTemplateExcelCsv } from "@/router/routeNames";
import { getExportNumFormat, getExportHorizontalAlignement } from "@/filter/exportFormat";
import { exportHelper } from "@/utils/exportHelper";
import dimensionEnum from "@/model/dimensionEnum";

export default {
  name: ExportTemplateExcelCsv,
  components: {
    TableRenderer,
  },
  data: function () {
    return {
      datas: [],
      title: "",
      subtitles: [],
      forcedTitleSubtitleColspan: null,
      filename: "Reporting",
      hideGroupsByTableNumber: {},
      dataReady: true,
      orientationPaysage: true,
      reduceHeader: false,
    };
  },
  computed: {
    maxNbOfColumns() {
      return this.datas && this.datas.length
        ? Math.max(
          ...this.datas.map(
            (data) => data.dimensionFields.length + data.measureFields.length
          )
        )
        : 0;
    },
    titleSubtitleColspan() {
      return this.forcedTitleSubtitleColspan ?? this.maxNbOfColumns;
    },
    excelAttrTh() {
      return excelStyles.baseThAttr;
    },
    datasGroupedBySheetId() {
      return groupBy(this.datas, "sheetId");
    },
    sheetIds() {
      return Object.keys(this.datasGroupedBySheetId);
    },
    exportFormat() {
      return this.$route.params.expFormat;
    },
  },
  created() {
    this.populate(JSON.parse(localStorage.exportData));
  },
  mounted: async function () {
    this.removeHiddenRows();
    switch (this.exportFormat) {
      // Export Excel
      case 'excel':
        await this.createExcel();
        break;
      // Export CSV
      case 'csv':
        await this.createCsv();
        break;
      default:
        console.error(`Unknown or missing 'exportFormat': ${this.exportFormat}`);
    }
  },
  methods: {
    /** Renseigne les propriétés de la vue à partir des données d'export */
    async populate(exportData) {
      const self = this;
      exportData.tables.forEach(function (data, i) {
        if (!data.tableNumber) data.tableNumber = i.toString();
        data.dimensionFields = data.dimensionFields.map((col) => {
          col.tdAttr = (value, key, item) => {
            let tempAttr = excelStyles.getTdAttr(col, item);
            const siteCurrency = item[dimensionEnum.SITE_CURRENCY] || 'EUR';
            const exportNumFormat = getExportNumFormat(value, key, false, siteCurrency);
            const horizontalAlignement = getExportHorizontalAlignement(exportNumFormat, siteCurrency);
            tempAttr["data-t"] = "n";
            tempAttr["data-num-fmt"] = exportNumFormat;
            tempAttr["data-a-h"] = horizontalAlignement;
            if (!tempAttr["data-num-fmt"]) tempAttr["data-t"] = "s";
            return tempAttr;
          };

          col.formatter = (value, key, item) => {
            if (value === 0) return null;
            const siteCurrency = item[dimensionEnum.SITE_CURRENCY] || 'EUR';
            if (!getExportNumFormat(value, key, false, siteCurrency)) {
              return TableTools.Helper.valueFormatter(value, key, false, siteCurrency);
            }
            return value;
          };

          col.thAttr = (value, key, item) => {
            return excelStyles.getThAttr(col, item);
          };
          return col;
        });
        data.measureFields = data.measureFields.map((col) => {
          col.tdAttr = (value, key, item) => {
            let tempAttr = excelStyles.getTdAttr(col, item);
            const siteCurrency = item[dimensionEnum.SITE_CURRENCY] || 'EUR';
            const exportNumFormat = getExportNumFormat(value, key, true, siteCurrency);
            const horizontalAlignement = getExportHorizontalAlignement(exportNumFormat, siteCurrency);

            tempAttr["data-a-h"] = horizontalAlignement;
            if (exportNumFormat) {
              tempAttr["data-t"] = "n";
              tempAttr["data-num-fmt"] = exportNumFormat;
            } else if (!isNaN(value) && value !== null && value !== "") {
              tempAttr["data-t"] = "n";
            } else {
              tempAttr["data-t"] = "s";
            }
            return tempAttr;
          };

          col.formatter = (value, key, item) => {
            if (value === 0) return null;
            const siteCurrency = item[dimensionEnum.SITE_CURRENCY] || 'EUR';
            if (!getExportNumFormat(value, key, true, siteCurrency)) {
              return TableTools.Helper.valueFormatter(value, key, false, siteCurrency);
            }
            return value;
          };

          col.thAttr = (value, key, item) => {
            return excelStyles.getThAttr(col, item);
          };
          return col;
        });
        data.sheetId = self.constructSheetId(data.tableHeader, self.datas);
        self.datas.push(data);
      });
      self.title = exportData.title;
      if (exportData.subtitles && Array.isArray(exportData.subtitles))
        self.subtitles = exportData.subtitles;
      if (exportData.filename) self.filename = exportData.filename;
      if (exportData.hideGroupsByTableNumber)
        self.hideGroupsByTableNumber = exportData.hideGroupsByTableNumber;
      if (exportData.reduceHeader) self.reduceHeader = exportData.reduceHeader;
      if (exportData.forcedTitleSubtitleColspan) self.forcedTitleSubtitleColspan = exportData.forcedTitleSubtitleColspan;
    },
    /** Construit le nom de l'onglet d'après le header de la table (nom du site en général), en respectant :
     * - maximum 30 caractères (limitation d'excel)
     * - spécifique à un header de table : éviter de mettre dans le même onglet des sites différents qui aurait les mêmes 30 premiers caractères
     */
    constructSheetId(tableHeader, existingDatas) {
      if (tableHeader) {
        let shortHeader = tableHeader.substring(
          0,
          Math.min(tableHeader.length, 30)
        );
        let increment = 1;
        while (
          existingDatas.some(
            (data) =>
              data.sheetId === shortHeader && data.tableHeader !== tableHeader
          ) &&
          increment < 10
        ) {
          shortHeader = `${shortHeader.substring(
            0,
            shortHeader.length - 1
          )}${increment}`;
          increment++;
        }
        return shortHeader;
      } else {
        return "Reporting";
      }
    },
    /** Supprime dans le DOM les lignes qui ne doivent pas être affichées,
     * car table-to-excel ne tient pas compte de "display:none"
     */
    removeHiddenRows() {
      const tablesWithHiddenRows = Array.from(
        document.querySelectorAll(".hideGroups")
      );
      if (tablesWithHiddenRows && tablesWithHiddenRows.length) {
        tablesWithHiddenRows.forEach((table) => {
          const hiddenRows = Array.from(
            table.querySelectorAll("tr[class^=table-row-group-level-]")
          );
          if (hiddenRows && hiddenRows.length) {
            hiddenRows.forEach((row) => {
              if (row.parentNode) row.parentNode.removeChild(row);
            });
          }
        });
      }
    },
    async createExcel() {
      let wb = new ExcelJS.Workbook();
      this.sheetIds.forEach((sheetId) => {
        const sheet = document.getElementById(sheetId);
        wb = TableToExcel.tableToSheet(wb, sheet, {
          autoStyle: false,
          sheet: {
            name: exportHelper.getValidSheetName(sheetId),
          },
        }
        );
      });

      // Auto-ajustement des colonnes pour chaque worksheet
      wb.eachSheet((worksheet) => {
        worksheet.columns.forEach((column) => {
          let maxLength = 0;
          column.eachCell({ includeEmpty: true }, (cell) => {
            // Ignore les cellules fusionnées (header/subtitle) dans le calcul
            if (cell.isMerged) return;

            const cellValue = cell.value ? String(cell.value) : '';
            maxLength = Math.max(maxLength, cellValue.length);
          });
          // Définit la largeur : longueur max + padding, avec limites min/max
          column.width = Math.min(Math.max(maxLength, 10), 60) + 1;
        });
      });

      TableToExcel.save(wb, `${this.filename}.xlsx`);
      setTimeout(function () {
        window.close();
      }, 500);
    },
    async createCsv() {
      let allCsvContent = ["sep=,"];
      this.sheetIds.forEach((sheetId, index) => {
        const table = document.getElementById(sheetId);
        if (table) {
          // Séparateur entre les tableaux
          if (index > 0) {
            allCsvContent.push('');
            allCsvContent.push('');
          }
          // En-tête
          allCsvContent.push(`"=== ${sheetId} ==="`);
          // Convertion en CSV
          const sheet = XLSX.utils.table_to_sheet(table);
          const csv = XLSX.utils.sheet_to_csv(sheet);
          allCsvContent.push(csv);
        }
      });
      const finalCsv = allCsvContent.join('\n');
      const blob = new Blob([finalCsv], { type: 'text/csv;charset=utf-8;' });
      const fileName = `${this.filename}.csv`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(function () {
        window.close();
      }, 500);
    },

    /**
     * Return a valid worksheet name from a string, by following https://support.microsoft.com/en-us/office/rename-a-worksheet-3f1f7148-ee83-404d-8ef0-9ff99fbad1f9
     * @param {string} sheetName 
     */
    getValidSheetName(sheetName) {
      let result = sheetName;
      if (sheetName.toLowerCase() === 'history')
        return '_' + sheetName
      if (sheetName.length > 31)
        result = result.slice(0, 31)
      //On enlève tous les caractères interdits
      result = result.replaceAll(/\?|\*/g, '.')
        .replaceAll(/\/|\\|:/g, '-')
        .replaceAll('[', '(')
        .replaceAll(']', ')')
        .replaceAll(/^'|'$/g, '"')
      return result;
    }
  },
};
</script>